<template>
    <div class="modal-card card">
        <form>
            <div class="card-header">
                <!-- Title -->
                <h4 class="card-header-title">{{ activeTab === 'promote' ? 'Promote' : 'Edit' }} Deployment Jobs</h4>
                <!-- Close -->
                <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div class="card-body">
                <!-- Custom Tabs Navigation -->
                <ul class="nav nav-tabs custom-modal-tabs" role="tablist">
                    <li v-for="tab in visibleTabs" :key="tab.name" class="nav-item" role="presentation">
                        <button class="nav-link d-flex align-items-center" :class="{ active: activeTab === tab.name }"
                            @click="setActiveTab(tab.name)" type="button" role="tab">
                            <i v-if="tab.icon" :class="[`fa fa-${tab.icon}`, 'me-2']"></i>
                            <span>{{ tab.label || tab.name }}</span>
                            <i v-if="tab.name === 'config-changes'" class="fa fa-times ms-3"
                                @click.stop="closeConfigChangesTab" style="cursor: pointer; font-size: 0.85rem;"
                                aria-label="Close tab"></i>
                        </button>
                    </li>
                </ul>

                <!-- Tab Content -->
                <div class="tab-content">
                    <div v-for="tab in visibleTabs" :key="tab.name" class="tab-pane fade"
                        :class="{ 'show active': activeTab === tab.name }" role="tabpanel">
                        <div v-if="activeTab === tab.name && tab.name === 'edit'" class="edit-tab-content">

                            <div class="row mb-0">
                                <div class="col-md-6">
                                    <div class="mb-3">
                                        <label class="form-label fw-bold text-muted small">
                                            Deployment Name
                                            <span class="fa fa-circle-info text-muted"
                                                title="Name of the job stack deployment" data-bs-toggle="tooltip"
                                                data-bs-placement="right"></span>
                                        </label>
                                        <p class="mb-0">
                                            <span>
                                                {{ deployment?.name }}
                                            </span>
                                        </p>
                                    </div>
                                </div>
                                <div class="col-md-6">
                                    <div class="mb-3">
                                        <label class="form-label fw-bold text-muted small">
                                            Job Stack Name
                                            <span class="fa fa-circle-info text-muted" title="Name of the job stack"
                                                data-bs-toggle="tooltip" data-bs-placement="right"></span>
                                        </label>
                                        <p class="mb-0">
                                            <span>
                                                {{ deployment?.job_stack }}
                                            </span>
                                        </p>
                                    </div>
                                </div>
                                <div class="col-md-6">
                                    <div class="mb-3">
                                        <label class="form-label fw-bold text-muted small">
                                            Sandbox Name
                                            <span class="fa fa-circle-info text-muted"
                                                title="Name of the sandbox containing the jobs" data-bs-toggle="tooltip"
                                                data-bs-placement="right"></span>
                                        </label>
                                        <p class="mb-0">
                                            <span>
                                                {{ deployment?.sandbox }}
                                            </span>
                                        </p>
                                    </div>
                                </div>
                                <div class="col-md-6">
                                    <div class="mb-3">
                                        <label class="form-label fw-bold text-muted small">
                                            Total Jobs Created
                                            <span class="fa fa-circle-info text-muted"
                                                title="Total number of jobs created in this deployment"
                                                data-bs-toggle="tooltip" data-bs-placement="right"></span>
                                        </label>
                                        <p class="mb-0">
                                            <span>
                                                {{ jobs ? jobs.length : "-" }}
                                            </span>
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div v-if="jobReloadInProgress">
                                <hr class="my-3">
                                <div class="d-flex justify-content-center align-items-center" style="height: 100px;">
                                    <div class="spinner-border spinner-border-sm me-2" role="status">
                                        <span class="visually-hidden">Loading...</span>
                                    </div>
                                    <span>Loading jobs data...</span>
                                </div>
                            </div>
                            <div v-else>
                                <hr class="my-3">
                                <div>
                                    <h4 class="my-4">
                                        Configuration
                                        <span class="fa fa-circle-info text-muted"
                                            title="Configure the variable values for all jobs created in this deployment. The changes will be updated in vars.yaml file for each job."
                                            data-bs-toggle="tooltip" data-bs-placement="right"></span>
                                    </h4>
                                    <ClusterConfigEasyMode ref="clusterConfigComponent"
                                        :key="'cluster-config-' + (jobs ? jobs.length : 0) + '-' + (jobReloadInProgress ? 'loading' : 'loaded')"
                                        v-model="clusterConfig" :fieldInfo="fieldInfo"
                                        :cleanConfigFn="cleanClusterConfig" />
                                    <div v-if="hasExtractConfig && extractConfig" class="mt-5">
                                        <hr class="my-3">
                                        <h4 class="my-4">
                                            Edge Extract Configuration
                                            <span class="fa fa-circle-info text-muted"
                                                title="Configure the tenant_id and extract fields."
                                                data-bs-toggle="tooltip" data-bs-placement="right"></span>
                                        </h4>
                                        <EasyMode ref="extractConfigComponent"
                                            :key="'extract-config-' + (jobs ? jobs.length : 0) + '-' + (jobReloadInProgress ? 'loading' : 'loaded')"
                                            v-model="extractConfig" :editMode="true" />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div v-if="activeTab === tab.name && tab.name === 'config-changes'"
                            class="config-changes-tab-content">
                            <div v-if="configDiff.length > 0">
                                <div class="d-flex justify-content-between align-items-top mb-3">
                                    <div>
                                        <p class="mb-1" v-if="conflictDetails">
                                            <strong>Updated by:</strong>&nbsp;<span class="text-muted">{{
                                                conflictDetails.updated_by }}</span>
                                        </p>
                                        <p v-if="conflictDetails" class="mb-0">
                                            <strong>Updated at:</strong>&nbsp;<span class="text-muted">{{
                                                DateTimeFormat(conflictDetails.updated_at) }}</span>
                                        </p>
                                    </div>
                                    <button class="btn btn-sm btn-outline-danger" @click="closeConfigChangesTab"
                                        :style="{ height: '27px' }" type="button">
                                        <i class="fa fa-times me-1"></i> Close
                                    </button>
                                </div>
                                <div class="row mb-1">
                                    <div class="col-6">
                                        <p class="config-header text-primary fw-bold">Local Copy</p>
                                    </div>
                                    <div class="col-6">
                                        <p class="config-header text-primary fw-bold">Remote Copy</p>
                                    </div>
                                </div>
                                <div v-for="(diff, index) in configDiff" :key="index" class="mb-4">
                                    <p class="config-header text-primary fw-bold mb-2">
                                        {{ diff.jobName }}
                                        <i class="fa fa-arrow-right-long pt-1 mx-2" style="font-size: 0.7rem;"></i>
                                        {{ diff.prev.relative_path }}
                                    </p>
                                    <Diff mode="split" theme="light" language="plaintext" :prev="diff.prev.content"
                                        :current="diff.current.content" />
                                </div>
                            </div>
                            <div v-else class="text-center text-muted py-4">
                                <p>No configuration conflicts detected.</p>
                            </div>
                        </div>
                        <div v-if="activeTab === tab.name && tab.name === 'promote'" class="promote-tab-content">
                            <div v-if="jobReloadInProgress" class="d-flex justify-content-center align-items-center"
                                style="height: 200px;">
                                <div class="spinner-border spinner-border-sm me-2" role="status">
                                    <span class="visually-hidden">Loading...</span>
                                </div>
                                <span>Loading jobs data...</span>
                            </div>
                            <div v-else-if="!jobs || jobs.length === 0" class="text-center text-muted py-4">
                                <p>No jobs available for promotion.</p>
                            </div>
                            <div v-else class="row g-0" style="min-height: 400px;">
                                <!-- Left Sidebar: Job List with Checkboxes -->
                                <div class="col-md-3 border-end d-flex flex-column" style="max-height: 600px;">
                                    <div class="pe-3 pt-3 flex-grow-1" style="overflow-y: auto;">
                                        <h4 class="mb-3 d-flex align-items-center">
                                            Select Jobs
                                            <span class="fa fa-circle-info text-muted ms-2" style="font-size: 0.8rem;"
                                                title="Select one or more jobs to promote." data-bs-toggle="tooltip"
                                                data-bs-placement="right">
                                            </span>
                                        </h4>
                                        <div class="list-group list-group-flush">
                                            <div v-for="job in jobs" :key="job.jobId || job.jobDetails?.name"
                                                class="list-group-item list-group-item-action d-flex align-items-center job-list-item"
                                                :class="{ 'job-list-item-active': currentPromotion === (job.jobDetails?.name || job.jobId) }"
                                                @click="changeCurrentPromotion(job.jobDetails?.name || job.jobId)"
                                                style="cursor: pointer; transition: all 0.2s ease;">
                                                <input class="form-check-input me-3" type="checkbox"
                                                    :value="job.jobDetails?.name || job.jobId"
                                                    :checked="selectedJobs.includes(job.jobDetails?.name || job.jobId)"
                                                    @click.stop="toggleJobSelection(job.jobDetails?.name || job.jobId)"
                                                    :id="'job-checkbox-' + (job.jobDetails?.name || job.jobId)"
                                                    style="flex-shrink: 0; cursor: pointer;" />
                                                <span class="flex-grow-1 fw-medium"
                                                    style="cursor: pointer; user-select: none;">
                                                    {{ job.jobDetails?.name || job.jobId }}
                                                </span>
                                            </div>
                                        </div>
                                        <div class="mt-3 pt-1">
                                            <button class="btn btn-sm btn-outline-primary" @click="toggleSelectAll"
                                                :disabled="toggleInProgress" type="button">
                                                {{ allJobsSelected ? 'Deselect All' : 'Select All' }}
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <!-- Right Panel: Promotion Details -->
                                <div class="col-md-9" style="max-height: 600px; overflow-y: auto;">
                                    <div class="p-3">
                                        <div v-if="!currentPromotion" class="text-center text-muted py-5">
                                            <i class="fa fa-arrow-left fa-2x mb-3"></i>
                                            <p>Select a job from the list to view promotion details.</p>
                                        </div>
                                        <div v-else>
                                            <h4 class="mb-3">Promotion Details</h4>

                                            <div v-if="currentPromotion && promotions[currentPromotion]">
                                                <!-- Promotion Form Fields -->
                                                <form-fields :key="`promotion-fields-${currentPromotion}`"
                                                    :fields="currentPromotionInfo?.fields || []"
                                                    v-model="promotions[currentPromotion]" />

                                                <!-- Meters Table -->
                                                <div class="mt-4">
                                                    <div class="table-responsive">
                                                        <span>Meters</span>
                                                        <table class="table table-sm table-nowrap mt-2">
                                                            <thead>
                                                                <tr>
                                                                    <th class="col-sm-5">Label</th>
                                                                    <th class="col-sm-5">Metrics Data Channel</th>
                                                                    <th class="col-sm-5">Metrics Type</th>
                                                                    <th class="col-sm-5">Metrics Field</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                <tr v-for="(meter, index) in currentMeters"
                                                                    :key="index">
                                                                    <td class="col-sm-5 p-2">
                                                                        <input
                                                                            :list="'meterLabelOptions-' + currentPromotion"
                                                                            style="padding: 1px 7px;"
                                                                            class="form-control"
                                                                            v-model="currentMeters[index].label" />
                                                                        <datalist
                                                                            :id="'meterLabelOptions-' + currentPromotion">
                                                                            <option v-for="option in meterLabels"
                                                                                :key="option" :value="option">
                                                                                {{ option }}
                                                                            </option>
                                                                        </datalist>
                                                                    </td>
                                                                    <td class="col-sm-5 p-2">
                                                                        <input
                                                                            :list="'dataChannelOptions-' + currentPromotion"
                                                                            style="padding: 1px 7px;"
                                                                            class="form-control"
                                                                            v-model="currentMeters[index].metrics_data_channel"
                                                                            @change="updateMetricsType(index)" />
                                                                        <datalist
                                                                            :id="'dataChannelOptions-' + currentPromotion">
                                                                            <option v-for="option in currentDataChannel"
                                                                                :key="option" :value="option">
                                                                                {{ option }}
                                                                            </option>
                                                                        </datalist>
                                                                    </td>
                                                                    <td class="col-sm-5 p-2">
                                                                        <input style="padding: 1px 7px;"
                                                                            class="form-control" type="text"
                                                                            v-model="currentMeters[index].metrics_type" />
                                                                    </td>
                                                                    <td class="col-sm-5 p-2">
                                                                        <input style="padding: 1px 7px;"
                                                                            class="form-control" type="text"
                                                                            v-model="currentMeters[index].metrics_field" />
                                                                    </td>
                                                                    <td class="col-sm-1">
                                                                        <i @click="removeMeter(index)"
                                                                            class="fa fa-regular fa-trash-can"
                                                                            style="cursor: pointer;"></i>
                                                                    </td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                        <div class="">
                                                            <button type="button"
                                                                class="btn btn-sm btn-primary float-end m-3"
                                                                @click="onAddMeter"
                                                                :disabled="promotions[currentPromotion]?.detach_meters">
                                                                <span class="fa fa-plus"></span>
                                                                Add Meter
                                                            </button>
                                                        </div>
                                                    </div>

                                                    <!-- Warning Message -->
                                                    <div v-if="displayWarning()"
                                                        class="alert alert-warning mt-3 py-2 px-3" role="alert">
                                                        <span class="fa fa-warning"></span>
                                                        <span class="fw-bold"> WARNING: </span>
                                                        meters are not attached for the job <strong>'{{
                                                            promotions[currentPromotion]?.dst_job_name }}'</strong>
                                                    </div>
                                                </div>
                                            </div>
                                            <div v-else>
                                                <div class="alert alert-danger">
                                                    <span class="fa fa-exclamation-triangle"></span>
                                                    <span class="fw-bold"> ERROR: </span>
                                                    No promotion details found for the job <strong>'{{ currentPromotion
                                                    }}'</strong>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="card-footer d-flex justify-content-between">
                <span class="text-danger" style="max-width: 750px; overflow-wrap: break-word;">
                    {{ activeTab === 'promote' ? promoteError : editError }}
                </span>
                <div>
                    <template v-if="activeTab === 'config-changes'">
                        <button class="btn btn-primary me-3" @click="onReloadConfig()" type="button">
                            <div v-if="reloadInProgress" class="spinner-border spinner-border-sm" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                            <div v-else>
                                Reload Changes
                            </div>
                        </button>
                        <button class="btn btn-primary" @click="onOverwriteConfig()" type="button">
                            <div v-if="overWriteInProgress" class="spinner-border spinner-border-sm" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                            <div v-else>
                                Overwrite Current Changes
                            </div>
                        </button>
                    </template>
                    <template v-else-if="activeTab === 'promote'">
                        <button class="btn btn-primary me-3" @click="onPromoteClick()" type="button"
                            :disabled="promoteInProgress || !isCurrentPromotionValid || !currentPromotion">
                            <div v-if="promoteInProgress" class="spinner-border spinner-border-sm" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                            <div v-else class="d-flex align-items-center">
                                <span>Promote</span>
                                <span class="fa fa-circle-info text-light" style="font-size: 0.85rem; margin-left: 5px;"
                                    title="Promote the currently displayed job." data-bs-toggle="tooltip"
                                    data-bs-placement="top">
                                </span>
                            </div>
                        </button>
                        <button class="btn btn-primary" @click="onPromoteSelectedClick()" type="button"
                            :disabled="promoteSelectedInProgress || !areSelectedPromotionsValid || selectedJobs.length === 0">
                            <div v-if="promoteSelectedInProgress" class="spinner-border spinner-border-sm"
                                role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                            <div v-else class="d-flex align-items-center">
                                <span>Promote Selected</span>
                                <span class="fa fa-circle-info text-light" style="font-size: 0.85rem; margin-left: 5px;"
                                    title="Promote all selected jobs to their respective destination sandboxes."
                                    data-bs-toggle="tooltip" data-bs-placement="top">
                                </span>
                            </div>
                        </button>
                    </template>
                    <template v-else>
                        <button class="btn btn-success me-3" @click="onSaveClick()" type="button"
                            :disabled="saveInProgress || saveAndRunInProgress || !jobs || jobs.length === 0">
                            <div v-if="saveInProgress" class="spinner-border spinner-border-sm" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                            <div v-else class="d-flex align-items-center">
                                <span>Save</span>
                                <span class="fa fa-circle-info text-light" style="font-size: 0.85rem; margin-left: 5px;"
                                    title="Save the configuration changes for all jobs in this deployment. The changes will be updated in vars.yaml file for each job with UI markers."
                                    data-bs-toggle="tooltip" data-bs-placement="top">
                                </span>
                            </div>
                        </button>
                        <button class="btn btn-success" @click="onSaveAndRunClick()" type="button"
                            :disabled="saveInProgress || saveAndRunInProgress || !jobs || jobs.length === 0">
                            <div v-if="saveAndRunInProgress" class="spinner-border spinner-border-sm" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                            <div v-else class="d-flex align-items-center">
                                <span>Save & Run</span>
                                <span class="fa fa-circle-info text-light" style="font-size: 0.85rem; margin-left: 5px;"
                                    title="Save the configuration changes and immediately run all jobs in this deployment. The changes will be updated in vars.yaml file for each job with UI markers."
                                    data-bs-toggle="tooltip" data-bs-placement="top">
                                </span>
                            </div>
                        </button>
                    </template>
                </div>
            </div>
        </form>
    </div>
</template>

<script>
import ClusterConfigEasyMode from "../ClusterConfigEasyMode.vue";
import EasyMode from "../EasyMode.vue";
import YAML from "yaml";
import axios from "axios";
import { DateTimeFormat } from '../../utils/commonFunction';
import Fields from "../../utils/field-constants/jobPromotionCreateFields";
import formFields from '../FormFields.vue';

export default {
    components: {
        ClusterConfigEasyMode,
        EasyMode,
        formFields
    },
    data() {
        return {
            uiMarkerStart: '# === UI:CONFIGURABLE_VALUES:START ===',
            uiMarkerEnd: '# === UI:CONFIGURABLE_VALUES:END ===',
            editError: "",
            promoteError: "",
            saveInProgress: false,
            saveAndRunInProgress: false,
            jobs: null,
            jobNames: [],
            deployment: null,
            clusterConfig: null,
            extractConfig: null,
            hasExtractConfig: false,
            activeTab: "edit",
            fieldInfo: {
                graph_ttl: {
                    defaultValue: "inf",
                    placeholder: "inf",
                    editable: false,
                    tooltip: "This will hold the time to delete the node from its existing timestamp.",
                },
                create_cluster_weightage_map: {
                    defaultValue: {},
                    editable: false,
                    tooltip: "This will provide the weightage value per type~domain. This will help with the algorithm of choosing the clusterID.",
                },
                create_cluster_invalid_ids: {
                    defaultValue: [],
                    editable: false,
                    tooltip: "These nodes will be dropped and sent to the quarantined list",
                },
                create_cluster_ttl_maps: {
                    defaultValue: {},
                    editable: false,
                    tooltip: "This will provide the weightage value per type~domain. This will help with the algorithm of choosing the clusterID.",
                },
                choose_cluster_type: {
                    defaultValue: "c_type",
                    placeholder: "c_type",
                    editable: false,
                    tooltip: "This configured type will be assigned as the cluster's type.",
                },
                choose_cluster_domain: {
                    defaultValue: "c_domain",
                    placeholder: "c_domain",
                    editable: false,
                    tooltip: "This configured domain will be assigned as the cluster's domain.",
                }
            },
            tabItems: [
                {
                    name: "edit",
                    label: "Edit",
                    slot: "edit-tab",
                    icon: "edit",
                    visible: true
                },
                {
                    name: "config-changes",
                    label: "Config Changes",
                    slot: "config-changes-tab",
                    icon: "code-compare",
                    visible: false
                },
                {
                    name: "promote",
                    label: "Promote",
                    slot: "promote-tab",
                    icon: "arrow-up-from-bracket",
                    visible: true
                }
            ],

            remoteJobsInfo: {},
            currentJobsInfo: {},
            configDiff: [],
            conflictDetails: null,
            overWriteInProgress: false,
            reloadInProgress: false,
            jobReloadInProgress: false,
            // Store anchor names per job per field: { jobName: { fieldName: anchorName } }
            jobAnchors: {},

            // promotion data
            promotions: {},
            promotionsInfo: {},
            meterLabels: [],
            currentPromotion: "",
            selectedJobs: [], // Track selected jobs with checkboxes
            prodMeters: {}, // Store production meters for each job
            toggleInProgress: false,
            promoteInProgress: false,
            promoteSelectedInProgress: false,
        }
    },
    computed: {
        visibleTabs() {
            return this.tabItems.filter(tab => tab.visible);
        },
        pipelineJob() {
            return this.jobs?.find(job => job.jobDetails?.kind.toLowerCase() === 'pipeline');
        },
        sandboxListOptions() {
            return this.$store.state.sandbox.prodV2Sandboxes ? this.$store.state.sandbox.prodV2Sandboxes.map((sandbox) => {
                return { "label": sandbox.name, "value": sandbox.name };
            }) : [];
        },
        currentPromotionInfo() {
            if (!this.currentPromotion) return null;
            const info = this.promotionsInfo[this.currentPromotion];
            if (!info) return null;
            // Ensure fields array exists and is valid
            if (!info.fields || !Array.isArray(info.fields)) {
                return null;
            }
            // Ensure field options are arrays only for customSingleSelect fields (to prevent multiselect errors)
            // Checkboxes should have options as undefined (not an array) for proper rendering
            const safeFields = info.fields.map(field => {
                const safeField = { ...field };
                // Only set options to array for customSingleSelect fields
                if (safeField.type === 'customSingleSelect') {
                    if (safeField.options && !Array.isArray(safeField.options)) {
                        safeField.options = [];
                    } else if (!safeField.options) {
                        safeField.options = [];
                    }
                }
                // For other field types, leave options as is (undefined for checkboxes, etc.)
                return safeField;
            });
            return {
                ...info,
                fields: safeFields
            };
        },
        currentMeters() {
            if (!this.currentPromotion || !this.promotions[this.currentPromotion]) return [];
            return this.promotions[this.currentPromotion].meters || [];
        },
        currentDataChannel() {
            if (!this.currentPromotion || !this.currentPromotionInfo) return [];
            return this.currentPromotionInfo.dataChannel || [];
        },
        allJobsSelected() {
            if (!this.jobs || this.jobs.length === 0) return false;
            return this.jobs.every(job => {
                const jobId = job.jobDetails?.name || job.jobId;
                return this.selectedJobs.includes(jobId);
            });
        },
        isCurrentPromotionValid() {
            if (!this.currentPromotion || !this.promotions[this.currentPromotion]) {
                return false;
            }
            const promotion = this.promotions[this.currentPromotion];
            // Check required fields: dst_sandbox_name, dst_job_name, description
            return !!(promotion.dst_sandbox_name &&
                promotion.dst_job_name &&
                promotion.description);
        },
        areSelectedPromotionsValid() {
            if (!this.selectedJobs || this.selectedJobs.length === 0) {
                return false;
            }
            // Check if all selected promotions have required fields filled
            return this.selectedJobs.every(jobId => {
                const promotion = this.promotions[jobId];
                if (!promotion) return false;
                return !!(promotion.dst_sandbox_name &&
                    promotion.dst_job_name &&
                    promotion.description);
            });
        },
    },
    props: {
        modalData: Object,
    },
    beforeMount() {
        this.jobNames = JSON.parse(JSON.stringify(this.modalData?.jobNames || []));
        this.jobs = JSON.parse(JSON.stringify(this.modalData?.jobs));
        this.deployment = JSON.parse(JSON.stringify(this.modalData?.deployment));

        this.loadConfig();
        this.initializePromoteTab();
    },
    methods: {
        DateTimeFormat,
        loadConfig() {
            // Initialize empty clusterConfig
            let extractedConfig = {
                graph_ttl: "",
                create_cluster_weightage_map: {},
                create_cluster_invalid_ids: [],
                create_cluster_ttl_maps: {},
                choose_cluster_type: "",
                choose_cluster_domain: ""
            };
            // Initialize extract config
            let extractConfigObj = null;
            this.hasExtractConfig = false;
            // Initialize jobAnchors
            this.jobAnchors = {};

            // Iterate through all jobs and extract config from vars.yaml
            if (this.jobs && this.jobs.length > 0) {
                this.jobs.forEach(job => {
                    if (job?.jobDetails?.system_files) {
                        // Find vars.yaml file
                        const varsYamlFile = job.jobDetails.system_files.find(
                            file => file.relative_path === 'vars.yaml'
                        );

                        if (varsYamlFile?.content) {
                            try {
                                // Extract the configurable values section between markers
                                const configSection = this.extractConfigSection(varsYamlFile.content);

                                if (configSection) {
                                    // Extract anchor names from the YAML text (before parsing)
                                    const jobName = job.jobDetails?.name || job.jobId;
                                    const anchors = this.extractAllAnchorsFromYaml(configSection);
                                    if (Object.keys(anchors).length > 0) {
                                        this.jobAnchors[jobName] = anchors;
                                    }

                                    // Parse the extracted section as simple YAML
                                    const parsedYaml = YAML.parse(configSection);

                                    if (parsedYaml && typeof parsedYaml === 'object') {
                                        if (parsedYaml.choose_cluster_type && !extractedConfig.choose_cluster_type) {
                                            extractedConfig.choose_cluster_type = parsedYaml.choose_cluster_type;
                                            this.fieldInfo.choose_cluster_type.editable = true;
                                        }

                                        if (parsedYaml.choose_cluster_domain && !extractedConfig.choose_cluster_domain) {
                                            extractedConfig.choose_cluster_domain = parsedYaml.choose_cluster_domain;
                                            this.fieldInfo.choose_cluster_domain.editable = true;
                                        }

                                        if (parsedYaml.graph_ttl && !extractedConfig.graph_ttl) {
                                            extractedConfig.graph_ttl = parsedYaml.graph_ttl;
                                            this.fieldInfo.graph_ttl.editable = true;
                                        }

                                        if (parsedYaml.create_cluster_weightage_map &&
                                            Object.keys(extractedConfig.create_cluster_weightage_map).length === 0) {
                                            extractedConfig.create_cluster_weightage_map = parsedYaml.create_cluster_weightage_map;
                                            this.fieldInfo.create_cluster_weightage_map.editable = true;
                                        }

                                        if (parsedYaml.create_cluster_invalid_ids &&
                                            extractedConfig.create_cluster_invalid_ids.length === 0) {
                                            extractedConfig.create_cluster_invalid_ids = parsedYaml.create_cluster_invalid_ids;
                                            this.fieldInfo.create_cluster_invalid_ids.editable = true;
                                        }

                                        if (parsedYaml.create_cluster_ttl_maps &&
                                            Object.keys(extractedConfig.create_cluster_ttl_maps).length === 0) {
                                            extractedConfig.create_cluster_ttl_maps = parsedYaml.create_cluster_ttl_maps;
                                            this.fieldInfo.create_cluster_ttl_maps.editable = true;
                                        }

                                        // Check for tenant_id and extract fields
                                        if ((parsedYaml.tenant_id !== undefined || parsedYaml.extract !== undefined) && !extractConfigObj) {
                                            extractConfigObj = {};
                                            if (parsedYaml.tenant_id !== undefined) {
                                                extractConfigObj.tenant_id = parsedYaml.tenant_id;
                                            }
                                            if (parsedYaml.extract !== undefined) {
                                                extractConfigObj.extract = parsedYaml.extract;
                                            }
                                            this.hasExtractConfig = true;
                                        }
                                    }
                                }
                            } catch (e) {
                                console.warn(`Failed to parse vars.yaml for job ${job.jobId}:`, e);
                            }
                        }
                    }
                });
            }
            // Create a new object reference to ensure reactivity
            this.clusterConfig = { ...extractedConfig };
            // Set extract config as YAML string if it exists
            if (extractConfigObj) {
                this.extractConfig = YAML.stringify(extractConfigObj);
            } else {
                this.extractConfig = null;
            }
        },
        setActiveTab(tabName) {
            this.activeTab = tabName;
        },
        extractConfigSection(yamlContent) {
            if (!yamlContent || typeof yamlContent !== 'string') {
                return null;
            }

            const startMarker = this.uiMarkerStart;
            const endMarker = this.uiMarkerEnd;

            const startIndex = yamlContent.indexOf(startMarker);
            const endIndex = yamlContent.indexOf(endMarker);

            if (startIndex === -1 || endIndex === -1 || startIndex >= endIndex) {
                return null;
            }

            // Extract the section between markers (excluding the markers themselves)
            const sectionStart = startIndex + startMarker.length;
            const sectionEnd = endIndex;
            const section = yamlContent.substring(sectionStart, sectionEnd).trim();

            return section || null;
        },
        /**
         * Check if a job has UI markers with fields defined in fieldInfo
         * @param {Object} job - Job object with jobDetails
         * @returns {boolean} - True if job has UI markers with any fieldInfo fields
         */
        hasUIMarkersWithValues(job) {
            if (!job?.jobDetails?.system_files) {
                return false;
            }

            // Find vars.yaml file
            const varsYamlFile = job.jobDetails.system_files.find(
                file => file.relative_path === 'vars.yaml'
            );

            if (!varsYamlFile?.content) {
                return false;
            }

            // Extract the configurable values section between markers
            const configSection = this.extractConfigSection(varsYamlFile.content);

            if (!configSection) {
                return false;
            }

            try {
                // Parse the extracted section as YAML
                const parsedYaml = YAML.parse(configSection);

                if (!parsedYaml || typeof parsedYaml !== 'object') {
                    return false;
                }

                // Get all field names from fieldInfo
                const fieldInfoKeys = Object.keys(this.fieldInfo);

                // Check if any of the parsed YAML keys match fields in fieldInfo
                const hasFieldInfoFields = Object.keys(parsedYaml).some(key => {
                    return fieldInfoKeys.includes(key);
                });

                return hasFieldInfoFields;
            } catch (e) {
                console.warn(`Failed to parse config section for job ${job.jobId}:`, e);
                return false;
            }
        },
        /**
         * Extract all anchor names from YAML text for all configurable fields using parseDocument
         * @param {string} yamlText - YAML text content
         * @returns {Object} - Object with field names as keys and anchor names as values
         */
        extractAllAnchorsFromYaml(yamlText) {
            const anchors = {};
            if (!yamlText || typeof yamlText !== 'string') {
                return anchors;
            }

            try {
                // Use parseDocument to parse YAML and preserve anchor information
                const doc = YAML.parseDocument(yamlText);
                if (!doc || !doc.contents) {
                    return anchors;
                }

                // Get the root mapping node
                const rootNode = doc.contents;

                // Check if it's a map (kind can be 'MAP' or we check for items property)
                if (!rootNode.items || !Array.isArray(rootNode.items)) {
                    return anchors;
                }

                // Iterate through all items in the YAML map
                rootNode.items.forEach(item => {
                    if (item && item.key && item.value) {
                        const fieldName = item.key.value;
                        const valueNode = item.value;

                        // Check if the value node has an anchor
                        if (valueNode.anchor) {
                            anchors[fieldName] = valueNode.anchor;
                        }
                    }
                });
            } catch (e) {
                console.warn('Failed to parse YAML document for anchor extraction:', e);
            }
            return anchors;
        },
        async onSaveClick() {
            await this.saveConfiguration(this.clusterConfig, this.extractConfig);
        },
        async onSaveAndRunClick() {
            await this.saveConfigurationAndRun(this.clusterConfig, this.extractConfig);
        },
        cleanClusterConfig(config) {
            // Helper method to clean config
            if (!config || typeof config !== 'object') {
                return {};
            }

            const cleaned = {};

            if (config.graph_ttl) cleaned.graph_ttl = config.graph_ttl;
            if (config.choose_cluster_type) cleaned.choose_cluster_type = config.choose_cluster_type;
            if (config.choose_cluster_domain) cleaned.choose_cluster_domain = config.choose_cluster_domain;

            if (config.create_cluster_weightage_map && Object.keys(config.create_cluster_weightage_map).length > 0) {
                const weightageMap = {};
                Object.keys(config.create_cluster_weightage_map).forEach(key => {
                    if (config.create_cluster_weightage_map[key] !== undefined) {
                        const valueObj = config.create_cluster_weightage_map[key];
                        // object with type and value
                        if (valueObj && typeof valueObj === 'object' && valueObj.type !== undefined && valueObj.value !== null) {
                            weightageMap[key] = valueObj.value;
                        }
                    }
                });
                if (Object.keys(weightageMap).length > 0) {
                    cleaned.create_cluster_weightage_map = weightageMap;
                }
            }

            if (config.create_cluster_ttl_maps && Object.keys(config.create_cluster_ttl_maps).length > 0) {
                const ttlMaps = {};
                Object.keys(config.create_cluster_ttl_maps).forEach(key => {
                    if (config.create_cluster_ttl_maps[key] !== undefined) {
                        const valueObj = config.create_cluster_ttl_maps[key];
                        // object with type and value
                        if (valueObj && typeof valueObj === 'object' && valueObj.type !== undefined && valueObj.value !== null) {
                            ttlMaps[key] = valueObj.value;
                        }
                    }
                });
                if (Object.keys(ttlMaps).length > 0) {
                    cleaned.create_cluster_ttl_maps = ttlMaps;
                }
            }

            // Only include array if it has items
            if (Array.isArray(config.create_cluster_invalid_ids) && config.create_cluster_invalid_ids.length > 0) {
                cleaned.create_cluster_invalid_ids = config.create_cluster_invalid_ids;
            }

            return cleaned;
        },
        async saveConfiguration(config, extractConfig) {
            this.editError = "";
            this.saveInProgress = true;

            try {
                const result = await this.updateJobsConfig(config, extractConfig);

                // If there are conflicts, the config changes tab will be shown automatically
                if (result.hasConflicts) {
                    this.$store.dispatch("addToast", {
                        message: "Configuration conflicts detected. Please review the changes.",
                        type: "warning",
                    });
                    this.saveInProgress = false;
                    return;
                }

                if (result.total === 0) {
                    this.editError = "No jobs found with configurable values in markers.";
                } else if (result.successful === result.total) {
                    this.$store.dispatch("addToast", {
                        message: `Configuration saved successfully for all ${result.total} job(s).`,
                        type: "success",
                    });
                    // update jobs in detail view
                    if (this.modalData?.onRefreshJobs && typeof this.modalData.onRefreshJobs === 'function') {
                        this.modalData.onRefreshJobs();
                    }
                    // update jobs in modal and load config
                    await this.refreshJobsData();
                    this.loadConfig();
                    this.initializePromoteTab(); // to update vars.yaml content in promote tab
                } else if (result.successful > 0) {
                    this.editError = `Failed to update ${result.total - result.successful} out of ${result.total} job(s)`;
                    this.$store.dispatch("addToast", {
                        message: `Configuration saved for ${result.successful} job(s), but ${result.total - result.successful} job(s) failed.`,
                        type: "warning",
                    });
                } else {
                    this.editError = `Failed to update all ${result.total} job(s)`;
                    this.$store.dispatch("addToast", {
                        message: this.editError,
                        type: "error",
                    });
                }
            } catch (error) {
                this.editError = error.response?.data?.error || error.message || "Failed to save configuration";
                this.$store.dispatch("addToast", {
                    message: this.editError || "Something went wrong when saving configuration. Please contact support.",
                    type: "error",
                });
            } finally {
                this.saveInProgress = false;
            }
        },
        async saveConfigurationAndRun(config, extractConfig) {
            this.editError = "";
            this.saveAndRunInProgress = true;

            try {
                const result = await this.updateJobsConfig(config, extractConfig);

                // If there are conflicts, the config changes tab will be shown automatically
                if (result.hasConflicts) {
                    this.$store.dispatch("addToast", {
                        message: "Configuration conflicts detected. Please review the changes in the Config Changes tab.",
                        type: "warning",
                    });
                    this.saveAndRunInProgress = false;
                    return;
                }

                if (result.total === 0) {
                    this.editError = "No jobs found with configurable values in markers.";
                } else if (result.successful === result.total) {
                    this.$store.dispatch("addToast", {
                        message: `Configuration saved successfully for all job(s).`,
                        type: "success",
                    });

                    this.executePipelineJob();
                    this.$store.dispatch("hideModal");
                } else if (result.successful > 0) {
                    this.editError = `Failed to update ${result.total - result.successful} out of ${result.total} job(s)`;
                    this.$store.dispatch("addToast", {
                        message: `Configuration saved for ${result.successful} job(s), but ${result.total - result.successful} job(s) failed.`,
                        type: "warning",
                    });
                } else {
                    this.editError = `Failed to update all ${result.total} job(s)`;
                    this.$store.dispatch("addToast", {
                        message: this.editError,
                        type: "error",
                    });
                }
            } catch (error) {
                this.editError = error.response?.data?.error || error.message || "Failed to save configuration";
                this.$store.dispatch("addToast", {
                    message: this.editError || "Something went wrong when saving configuration. Please contact support.",
                    type: "error",
                });
            } finally {
                this.saveAndRunInProgress = false;
            }
        },
        /**
         * Update the config section in vars.yaml while preserving content outside markers and extra fields inside
         * @param {string} yamlContent - Original vars.yaml content
         * @param {Object} newConfig - New config values from UI
         * @param {string} jobName - Job name to get anchor names for
         * @param {string} extractConfigYaml - Extract config as YAML string (optional)
         * @returns {string} - Updated vars.yaml content
         */
        updateConfigSection(yamlContent, newConfig, jobName, extractConfigYaml = null) {
            const startMarker = this.uiMarkerStart;
            const endMarker = this.uiMarkerEnd;

            const startIndex = yamlContent.indexOf(startMarker);
            const endIndex = yamlContent.indexOf(endMarker);

            // If markers don't exist, return original content
            if (startIndex === -1 || endIndex === -1 || startIndex >= endIndex) {
                return yamlContent;
            }

            // Extract content before and after markers
            const beforeSection = yamlContent.substring(0, startIndex);
            const afterSection = yamlContent.substring(endIndex + endMarker.length);

            // Extract and parse existing config section
            const sectionStart = startIndex + startMarker.length;
            const sectionEnd = endIndex;
            const existingSection = yamlContent.substring(sectionStart, sectionEnd).trim();

            // Get stored anchor names for this job, or extract from existing section if not stored
            let jobAnchors = this.jobAnchors[jobName] || {};
            if (!jobAnchors || Object.keys(jobAnchors).length === 0) {
                // Fallback: extract anchors from existing section if not already stored
                jobAnchors = this.extractAllAnchorsFromYaml(existingSection);
            }

            let existingConfig = {};
            if (existingSection) {
                try {
                    existingConfig = YAML.parse(existingSection) || {};
                } catch (e) {
                    console.warn('Failed to parse existing config section:', e);
                    existingConfig = {};
                }
            }

            // Only update fields that already exist in the config section
            // Filter newConfig to only include keys that exist in existingConfig
            // This ensures we don't add new fields, only update existing ones
            const filteredNewConfig = {};
            Object.keys(newConfig).forEach(key => {
                if (Object.prototype.hasOwnProperty.call(existingConfig, key)) {
                    // Only update if the field exists in the original config
                    // Update even if the new value is empty/null (user might want to clear it)
                    filteredNewConfig[key] = newConfig[key];
                }
            });

            // Merge filtered new config with existing config (only update existing fields, preserve all others)
            let mergedConfig = {
                ...existingConfig,
                ...filteredNewConfig
            };

            // Check if tenant_id or extract fields exist in the original config
            let edgeExtractConfigAvailable = false;
            if (extractConfigYaml) {
                if (existingConfig.tenant_id !== undefined || existingConfig.extract !== undefined) {
                    edgeExtractConfigAvailable = true;

                    // Delete tenant_id and extract from config if extract config is provided and they exist in original config
                    delete mergedConfig.tenant_id;
                    delete mergedConfig.extract;
                }
            }

            // Convert merged config back to YAML string with anchor format
            // Uses stored anchor names if available, otherwise falls back to field name
            const formatYamlWithAnchor = (key, value) => {
                // Get anchor name for this field, fallback to key if not found
                const anchorName = jobAnchors[key] || key;

                // Handle simple values (string, number, boolean, null)
                if (value === null || value === undefined) {
                    return `${key}: &${anchorName} `;
                } else if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
                    return `${key}: &${anchorName} ${value}`;
                } else if (Array.isArray(value)) {
                    // For arrays, format as JSON string inline
                    const jsonStr = JSON.stringify(value);
                    return `${key}: &${anchorName} ${jsonStr}`;
                } else if (typeof value === 'object') {
                    // For objects, format as JSON string inline
                    const jsonStr = JSON.stringify(value);
                    return `${key}: &${anchorName} ${jsonStr}`;
                }
                return `${key}: &${anchorName} ${String(value)}`;
            };

            let updatedSection = Object.keys(mergedConfig)
                .map(key => formatYamlWithAnchor(key, mergedConfig[key]))
                .join('\n');

            // append the easy mode edge extract yaml with anchors
            if (extractConfigYaml && edgeExtractConfigAvailable) {
                try {
                    const lines = extractConfigYaml.split('\n');
                    const processedLines = [];
                    
                    for (let i = 0; i < lines.length; i++) {
                        const line = lines[i];
                        const trimmedLine = line.trim();
                        
                        // Check if this line is a field we need to add anchor to
                        if (trimmedLine.startsWith('tenant_id:') || trimmedLine.startsWith('extract:')) {
                            const fieldName = trimmedLine.split(':')[0].trim();
                            const anchorName = jobAnchors[fieldName] || fieldName;
                            
                            // Check if anchor is already inline
                            if (trimmedLine.includes('&')) {
                                processedLines.push(line);
                            } else {
                                // Check if next line has an anchor
                                if (i + 1 < lines.length && lines[i + 1].trim().startsWith('&')) {
                                    // Combine current line with anchor from next line
                                    const indent = line.match(/^(\s*)/)[1];
                                    processedLines.push(`${indent}${fieldName}: &${anchorName}`);
                                    i++; // Skip the next line since we've merged it
                                } else {
                                    // Add anchor inline to current line
                                    processedLines.push(line.replace(new RegExp(`^\\s*${fieldName}:`), `${line.match(/^(\s*)/)[1]}${fieldName}: &${anchorName}`));
                                }
                            }
                        } else {
                            processedLines.push(line);
                        }
                    }
                    
                    const extractYamlWithAnchors = processedLines.join('\n');
                    updatedSection = updatedSection + '\n' + extractYamlWithAnchors;
                } catch (e) {
                    console.warn('Failed to add anchors to extract config YAML:', e);
                    // Fallback: append as-is if parsing fails
                    updatedSection = updatedSection + '\n' + extractConfigYaml;
                }
            }

            // Reconstruct the full vars.yaml content
            const updatedContent = beforeSection +
                startMarker + '\n' +
                '# Managed by UI - manual edits may be overwritten\n' +
                updatedSection + '\n' +
                endMarker +
                afterSection;
            return updatedContent;
        },
        async updateJobsConfig(newConfig, extractConfigYaml = null) {
            if (!this.jobs || this.jobs.length === 0) {
                return {
                    total: 0,
                    successful: 0
                };
            }

            // Filter jobs that have vars.yaml with config markers
            const jobsToUpdate = this.jobs.filter(job => {
                if (!job?.jobDetails?.system_files) {
                    return false;
                }
                const varsYamlFile = job.jobDetails.system_files.find(
                    file => file.relative_path === 'vars.yaml'
                );
                if (!varsYamlFile?.content) {
                    return false;
                }
                // Check if markers exist
                const hasMarkers = varsYamlFile.content.includes(this.uiMarkerStart) &&
                    varsYamlFile.content.includes(this.uiMarkerEnd);
                return hasMarkers;
            });

            if (jobsToUpdate.length === 0) {
                return {
                    total: 0,
                    successful: 0
                };
            }

            const cid = this.$store.state.currentClient?.cid;
            const sandboxName = this.deployment?.sandbox || this.modalData?.sandboxName;
            const lakeviewUrl = this.$store.state.lakeviewUrl;
            const accessToken = this.$store.state.accessToken;

            // Reset config diff and conflict details
            this.configDiff = [];
            this.conflictDetails = null;

            // Construct payload and update the current jobs info
            const currentJobsMap = {};
            jobsToUpdate.forEach((job) => {
                const varsYamlFile = job.jobDetails.system_files.find(
                    file => file.relative_path === 'vars.yaml'
                );

                const jobName = job.jobDetails?.name || job.jobId;

                // Update the vars.yaml content
                const updatedContent = this.updateConfigSection(
                    varsYamlFile.content,
                    newConfig,
                    jobName,
                    extractConfigYaml
                );

                // Update the job object
                const updatedJob = {
                    ...job.jobDetails,
                    system_files: job.jobDetails.system_files.map(file => {
                        if (file.relative_path === 'vars.yaml') {
                            return {
                                ...file,
                                content: updatedContent
                            };
                        }
                        return file;
                    })
                };
                currentJobsMap[updatedJob.name] = updatedJob;
            });
            this.currentJobsInfo = currentJobsMap;

            // Get all jobs from remote to check for conflicts
            const getJobsPromises = jobsToUpdate.map((job) => {
                const jobName = job.jobDetails?.name || job.jobId;
                return axios.get(
                    `${lakeviewUrl}/v1/cids/${cid}/sandboxes/${sandboxName}/jobs/${jobName}`,
                    {
                        headers: {
                            Authorization: `Bearer ${accessToken}`,
                        },
                    }
                ).then(response => {
                    return {
                        jobName: jobName,
                        data: response.data
                    };
                }).catch(error => {
                    return {
                        jobName: jobName,
                        error: error
                    };
                });
            });

            // Wait for all get jobs to complete using Promise.allSettled
            const results = await Promise.allSettled(getJobsPromises);
            const remoteJobsMap = {};
            let hasConflicts = false;
            const diff = [];

            results.forEach((result, index) => {
                const job = jobsToUpdate[index];
                const jobName = job.jobDetails?.name || job.jobId;

                if (result.status === 'fulfilled' && result.value.data) {
                    const remoteJob = result.value.data;
                    remoteJobsMap[jobName] = remoteJob;

                    const currentJob = currentJobsMap[jobName];

                    // Check if job was updated since we last fetched it
                    if (remoteJob.updated_at !== job.jobDetails.updated_at) {
                        hasConflicts = true;

                        // Compare all files (system_files and user_files) between current and remote
                        ["system_files", "user_files"].forEach((fileType) => {
                            const currentFiles = currentJob[fileType] || [];
                            const remoteFiles = remoteJob[fileType] || [];

                            // Check files that exist in remote (may have been updated or are new)
                            remoteFiles.forEach((remoteFile) => {
                                const currentFile = currentFiles.find(
                                    f => f.relative_path === remoteFile.relative_path
                                );

                                // If file doesn't exist in current, it's a new file in remote
                                if (!currentFile) {
                                    diff.push({
                                        jobName: jobName,
                                        prev: {
                                            relative_path: remoteFile.relative_path,
                                            content: ''
                                        },
                                        current: {
                                            relative_path: remoteFile.relative_path,
                                            content: remoteFile.content || ''
                                        }
                                    });
                                } else if (currentFile.content !== remoteFile.content) {
                                    // File exists in both but content differs
                                    diff.push({
                                        jobName: jobName,
                                        prev: {
                                            relative_path: currentFile.relative_path,
                                            content: currentFile.content || ''
                                        },
                                        current: {
                                            relative_path: remoteFile.relative_path,
                                            content: remoteFile.content || ''
                                        }
                                    });
                                }
                            });

                            // Check files that exist in current but not in remote (may have been deleted)
                            currentFiles.forEach((currentFile) => {
                                const remoteFile = remoteFiles.find(
                                    f => f.relative_path === currentFile.relative_path
                                );

                                if (!remoteFile) {
                                    // File exists in current but not in remote (deleted in remote)
                                    diff.push({
                                        jobName: jobName,
                                        prev: {
                                            relative_path: currentFile.relative_path,
                                            content: currentFile.content || ''
                                        },
                                        current: {
                                            relative_path: currentFile.relative_path,
                                            content: ''
                                        }
                                    });
                                }
                            });
                        });
                    }
                }
            });

            this.remoteJobsInfo = remoteJobsMap;
            this.configDiff = diff;

            // If there are conflicts, show the config changes tab
            if (hasConflicts && diff.length > 0) {
                this.tabItems[1].visible = true;
                // Get the most recent update details
                const latestRemoteJob = Object.values(remoteJobsMap)
                    .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))[0];
                if (latestRemoteJob) {
                    this.conflictDetails = {
                        updated_by: latestRemoteJob.updated_by || 'Unknown',
                        updated_at: latestRemoteJob.updated_at
                    };
                }
                // Switch to config changes tab
                this.setActiveTab('config-changes');
                return {
                    total: jobsToUpdate.length,
                    successful: 0,
                    hasConflicts: true
                };
            }

            // No conflicts or force update - proceed with actual updates
            const updatePromises = Object.values(currentJobsMap).map((updatedJob) => {
                return axios.put(
                    `${lakeviewUrl}/v1/cids/${cid}/sandboxes/${sandboxName}/jobs/${updatedJob.name}`,
                    updatedJob,
                    {
                        headers: {
                            Authorization: `Bearer ${accessToken}`,
                        },
                    }
                );
            });

            // Wait for all updates to complete using Promise.allSettled
            const updateResults = await Promise.allSettled(updatePromises);
            let successfulUpdates = 0;

            updateResults.forEach((result) => {
                if (result.status === 'fulfilled') {
                    successfulUpdates++;
                } else {
                    console.error('Failed to update job: ', result.reason.response?.data);
                }
            });

            return {
                total: jobsToUpdate.length,
                successful: successfulUpdates,
                hasConflicts: false
            };
        },
        async executePipelineJob() {
            if (!this.pipelineJob || !this.pipelineJob.jobDetails) {
                console.log('No pipeline job found..');
                return;
            }
            try {
                const cid = this.$store.state.currentClient?.cid;
                const sandboxName = this.deployment?.sandbox || this.modalData?.sandboxName;
                const response = await axios.post(
                    `${this.$store.state.lakeviewUrl}/v1/cids/${cid}/sandboxes/${sandboxName}/jobs/${this.pipelineJob.jobDetails.name}/executions`, {},
                    {
                        headers: {
                            Authorization: `Bearer ${this.$store.state.accessToken}`,
                        },
                    }
                );
                this.$router.push({
                    name: "Sandbox Job Execution Details",
                    params: {
                        cid: cid,
                        sandboxId: sandboxName,
                        jobId: this.pipelineJob.jobDetails.name,
                        executionId: response.data.execution_id
                    }
                });
            } catch (error) {
                this.editError = error.response?.data?.error || error.message;
                this.$store.dispatch("addToast", {
                    message: error.response?.data?.error || error.message || "Failed to execute pipeline job. Please contact support.",
                    type: "error",
                });
            }
        },
        async onReloadConfig() {
            this.closeConfigChangesTab();

            // reload jobs in detail view
            if (this.modalData?.onRefreshJobs && typeof this.modalData.onRefreshJobs === 'function') {
                this.modalData.onRefreshJobs();
            }

            // reload jobs in modal
            await this.refreshJobsData();
            this.loadConfig();
            this.initializePromoteTab(); // to update vars.yaml content in promote tab  
        },
        async onOverwriteConfig() {
            this.editError = "";
            this.saveInProgress = true;
            this.saveAndRunInProgress = false;
            this.overWriteInProgress = true;

            const cid = this.$store.state.currentClient?.cid;
            const sandboxName = this.deployment?.sandbox || this.modalData?.sandboxName;
            const lakeviewUrl = this.$store.state.lakeviewUrl;
            const accessToken = this.$store.state.accessToken;

            try {
                // Create update promises for all jobs in currentJobsInfo
                const updatePromises = Object.values(this.currentJobsInfo).map((updatedJob) => {
                    return axios.put(
                        `${lakeviewUrl}/v1/cids/${cid}/sandboxes/${sandboxName}/jobs/${updatedJob.name}`,
                        updatedJob,
                        {
                            headers: {
                                Authorization: `Bearer ${accessToken}`,
                            },
                        }
                    );
                });

                // Wait for all updates to complete using Promise.allSettled
                const updateResults = await Promise.allSettled(updatePromises);
                let successfulUpdates = 0;
                const total = Object.keys(this.currentJobsInfo).length;

                updateResults.forEach((result) => {
                    if (result.status === 'fulfilled') {
                        successfulUpdates++;
                    } else {
                        console.error('Failed to update job: ', result.reason.response?.data);
                    }
                });

                // Show toasts based on results
                if (successfulUpdates === total) {
                    this.$store.dispatch("addToast", {
                        message: `Configuration overwritten successfully for all ${total} job(s).`,
                        type: "success",
                    });

                    // update jobs in detail view
                    if (this.modalData?.onRefreshJobs && typeof this.modalData.onRefreshJobs === 'function') {
                        this.modalData.onRefreshJobs();
                    }

                    // update jobs in modal
                    await this.refreshJobsData();
                    this.loadConfig();
                    this.initializePromoteTab(); // to update vars.yaml content in promote tab

                    // Hide config changes tab after successful overwrite
                    this.tabItems[1].visible = false;
                    this.setActiveTab('edit');
                    // Clear the diff data
                    this.configDiff = [];
                    this.conflictDetails = null;
                } else if (successfulUpdates > 0) {
                    this.editError = `Failed to overwrite ${total - successfulUpdates} out of ${total} job(s)`;
                    this.$store.dispatch("addToast", {
                        message: `Configuration overwritten for ${successfulUpdates} job(s), but ${total - successfulUpdates} job(s) failed.`,
                        type: "warning",
                    });
                } else {
                    this.editError = `Failed to overwrite all ${total} job(s)`;
                    this.$store.dispatch("addToast", {
                        message: this.editError,
                        type: "error",
                    });
                }
            } catch (error) {
                this.editError = error.response?.data?.error || error.message || "Failed to overwrite configuration";
                this.$store.dispatch("addToast", {
                    message: this.editError || "Something went wrong when overwriting configuration. Please contact support.",
                    type: "error",
                });
            } finally {
                this.saveInProgress = false;
                this.saveAndRunInProgress = false;
                this.overWriteInProgress = false;
            }
        },
        closeConfigChangesTab() {
            // Hide the config changes tab and switch back to edit tab
            this.tabItems[1].visible = false;
            this.setActiveTab('edit');
            // Clear the diff data
            this.configDiff = [];
            this.conflictDetails = null;
        },
        async readSandboxJobDetails(sandboxName, jobName) {
            const cid = this.$store.state.currentClient?.cid;
            return axios.get(this.$store.state.lakeviewUrl + `/v1/cids/${cid}/sandboxes/${sandboxName}/jobs/${jobName}`, {
                headers: {
                    Authorization: `Bearer ${this.$store.state.accessToken}`,
                },
            })
        },
        async refreshJobsData() {
            if (!this.jobNames || this.jobNames.length === 0) {
                console.log('No job names to refresh jobs data...');
                this.jobs = [];
                return;
            }

            this.jobReloadInProgress = true;
            try {
                const details = await Promise.all(
                    this.jobNames.map(async (jobId) => {
                        try {
                            const response = await this.readSandboxJobDetails(this.deployment.sandbox, jobId);
                            return {
                                jobId: jobId,
                                jobDetails: response?.data || null,
                                error: null
                            };
                        } catch (error) {
                            return {
                                jobId: jobId,
                                jobDetails: null,
                                error: error.response?.data?.error || error.message
                            };
                        }
                    })
                );
                // Replace the entire array to ensure Vue detects the change
                this.jobs = details;
            } catch (error) {
                this.jobs = [];
                console.error('Error refreshing jobs data:', error);
            } finally {
                this.jobReloadInProgress = false;
            }
        },

        // promotion functions
        async initializePromoteTab() {
            if (!this.jobs || this.jobs.length === 0) {
                console.log('No jobs to initialize promote tab...');
                return;
            }

            // Initialize selectedJobs array
            this.selectedJobs = [];

            // Process all jobs in parallel using Promise.all
            await Promise.all(
                this.jobs.map(async (job) => {
                    const jobDetails = job.jobDetails;
                    const promoteObject = {
                        dst_sandbox_name: this.$store.state.sandbox.prodV2Sandboxes.length ? this.$store.state.sandbox.prodV2Sandboxes[0].name : "",
                        dst_job_name: jobDetails.name,
                        description: `Promote - '${jobDetails.name}'`,
                        src_job_run_id: "",
                        vars: jobDetails?.system_files?.find(file => file.relative_path === 'vars.yaml')?.content || ""
                    }

                    let fields = [];
                    let promoteFields = JSON.parse(JSON.stringify(Fields));
                    for (const field of promoteFields) {
                        // Create a deep copy of each field to avoid sharing references
                        const fieldCopy = JSON.parse(JSON.stringify(field));

                        if (fieldCopy.name === "dst_sandbox_name") {
                            // Create a new array copy for options
                            fieldCopy.options = JSON.parse(JSON.stringify(this.sandboxListOptions));
                        } else if (fieldCopy.name === "src_job_run_id") {
                            // Only assigning recent 15 job run ids - get job-specific options
                            const options = await this.getJobRunIdOptions(jobDetails.name);

                            // Ensure options is always an array - create a new array copy for options
                            if (Array.isArray(options) && options.length > 0) {
                                fieldCopy.options = JSON.parse(JSON.stringify(options.slice(0, 15)));
                                // set the latest job run id as the default value
                                promoteObject.src_job_run_id = options[0].value;
                            } else {
                                fieldCopy.options = [];
                            }
                        } else if (fieldCopy.name === "dst_job_name") {
                            fieldCopy.options = [{
                                label: promoteObject.dst_job_name,
                                value: promoteObject.dst_job_name
                            }];
                        } else if (fieldCopy.name === "disable_meters_validation") {
                            fieldCopy.editable = this.$store.getters.userRoles.isAqferAdmin;
                            fieldCopy.visible = this.$store.getters.userRoles.isAqferAdmin;
                        }
                        fields.push(fieldCopy);
                    }

                    const promotionObj = JSON.parse(JSON.stringify(promoteObject));
                    // Initialize meters array in promotion object
                    promotionObj.meters = [];
                    this.promotions[jobDetails.name] = promotionObj;

                    // Deep copy fields to prevent mutations and ensure all options are arrays
                    const fieldsCopy = JSON.parse(JSON.stringify(fields));
                    // Ensure all field options are arrays to prevent multiselect errors
                    fieldsCopy.forEach(field => {
                        if (field.type === 'customSingleSelect') {
                            if (field.options && !Array.isArray(field.options)) {
                                field.options = [];
                            } else if (!field.options) {
                                field.options = [];
                            }
                        }
                    });

                    this.promotionsInfo[jobDetails.name] = {
                        fields: fieldsCopy,
                        dataChannel: [],
                        dataChannelTypeMap: new Map(),
                        dataChannelMap: new Map(), // to cache counts request
                        latestRunIdMap: new Map(), // to cache latest successful job run id
                        saveMeters: [], // Store saved meters when detach_meters is enabled
                    };

                    // Initialize production meters for each job
                    this.prodMeters[jobDetails.name] = [];

                    // Set up watchers for this job's promotion fields (like PromoteJob.vue)
                    this.setupPromotionWatchers(jobDetails.name);

                    await this.getDataChannels(jobDetails.name, promoteObject.src_job_run_id);

                    // Auto-select jobs that have UI markers with values
                    if (this.hasUIMarkersWithValues(job)) {
                        const jobId = jobDetails?.name || job.jobId;
                        if (jobId && !this.selectedJobs.includes(jobId)) {
                            this.selectedJobs.push(jobId);
                        }
                    }
                })
            );

            // Get meter labels after all jobs are initialized
            await this.getMeterLabels();
        },
        async getJobRunIdOptions(jobId) {
            let sandbox = this.deployment.sandbox
            let response = await axios.get(`${this.$store.state.lakeviewUrl}/v1/cids/${this.$store.state.currentClient.cid}/sandboxes/${sandbox}/jobs/${jobId}/executions?status=SUCCEEDED`, {
                headers: {
                    Authorization: `Bearer ${this.$store.state.accessToken}`,
                },
            })
            if (response.data?.length > 0) {
                return response.data.map(run => {
                    return {
                        label: `${run.id} - ${DateTimeFormat(run.completed_at)}`,
                        value: run.id
                    }
                })
            }
            return []
        },
        async getMeterLabels() {
            await axios.get(`${this.$store.state.lakeviewUrl}/v1/meter-labels`, {
                headers: {
                    Authorization: `Bearer ${this.$store.state.accessToken}`,
                },
            })
                .then((response) => {
                    if (response.data?.meter_labels) {
                        this.meterLabels = response.data.meter_labels
                    }
                })
        },
        async getDataChannels(jobId, jobRunId) {
            let sandbox = this.deployment.sandbox
            let job = jobId
            if (!jobRunId) {
                sandbox = this.promotions[jobId].dst_sandbox_name
                job = this.promotions[jobId].dst_job_name
                jobRunId = await this.getLatestSuccessfulRunId(jobId);
            }
            if (sandbox && job && jobRunId)
                await this.getDataChannelsForJobRunId(sandbox, job, jobRunId)
        },
        async getDataChannelsForJobRunId(sandbox, job, jobRunId) {
            if (this.promotionsInfo[job].dataChannelMap.has(jobRunId)) { // check cache
                this.promotionsInfo[job].dataChannel = this.promotionsInfo[job].dataChannelMap.get(jobRunId).dataChannel
                return
            }
            let countsPayload = {};
            countsPayload.filter_dimensions = {
                "cid": this.$route.params.cid,
                "sandbox_name": sandbox,
                "job_name": job,
                "job_run_id": jobRunId
            }
            let metricsPayload = JSON.parse(JSON.stringify(this.$store.state.amdp.countsMetricReqObj));
            delete metricsPayload["amdp-performance-metric"];
            delete metricsPayload["amdp-job-run-params"];
            if (metricsPayload && metricsPayload["amdp-metric"]) {
                metricsPayload["amdp-metric"].header = ["data_channel", "data_channel_type"]
            }
            countsPayload.metrics = metricsPayload
            axios.post(`${this.$store.state.countsUrl}/v2/cids/${this.$store.state.currentClient.cid}/metrics/batch/query`, countsPayload, {
                headers: {
                    Authorization: `Bearer ${this.$store.state.accessToken}`,
                },
            }).then((response) => {
                const dataChannelSet = new Set();
                const dataChannelTypeMap = new Map();
                response.data.data["amdp-metric"].data.forEach(subArray => {
                    const [element1, element2] = subArray; // Destructure the first two elements
                    dataChannelSet.add(element1);        // Add data channel to the Set
                    dataChannelTypeMap.set(element1, element2);    // Map data channel to its type
                });
                this.promotionsInfo[job].dataChannel = [...dataChannelSet].sort()
                this.promotionsInfo[job].dataChannelTypeMap = dataChannelTypeMap
                this.promotionsInfo[job].dataChannelMap.set(jobRunId, { dataChannel: this.promotionsInfo[job].dataChannel, dataChannelTypeMap: this.promotionsInfo[job].dataChannelTypeMap });
            })
        },
        async getLatestSuccessfulRunId(jobId) {
            let id = ""
            let cid = this.$store.state.currentClient.cid
            let sandbox = this.promotions[jobId].dst_sandbox_name
            let job = this.promotions[jobId].dst_job_name
            if (this.promotionsInfo[jobId].latestRunIdMap.has(sandbox + '~' + job)) {
                let data = this.promotionsInfo[jobId].latestRunIdMap.get(sandbox + '~' + job)
                return data
            }
            if (cid && sandbox && job) {
                await axios.get(`${this.$store.state.lakeviewUrl}/v1/cids/${cid}/sandboxes/${sandbox}/jobs/${job}/executions?status=SUCCEEDED&limit=1`, {
                    headers: {
                        Authorization: `Bearer ${this.$store.state.accessToken}`,
                    },
                })
                    .then((response) => {
                        if (response.data.length > 0 && response.data[0].id) {
                            id = response.data[0].id
                            this.promotionsInfo[jobId].latestRunIdMap.set(sandbox + '~' + job, id)
                        }
                    })
                    .catch((error) => {
                        console.log('Error getting latest successful run id:', error);
                    });
            }
            return id
        },
        changeCurrentPromotion(jobId) {
            this.currentPromotion = jobId;
            // Initialize meters if not already loaded
            if (jobId) {
                if (!this.promotions[jobId]) {
                    return;
                }
                // Initialize meters array in promotion if not present
                if (!this.promotions[jobId].meters) {
                    this.promotions[jobId].meters = [];
                }
                // Initialize saveMeters in promotionsInfo if not present
                if (!this.promotionsInfo[jobId]) {
                    this.promotionsInfo[jobId] = { saveMeters: [] };
                } else if (!this.promotionsInfo[jobId].saveMeters) {
                    this.promotionsInfo[jobId].saveMeters = [];
                }
                // Initialize production meters if not present
                if (!this.prodMeters[jobId]) {
                    this.prodMeters[jobId] = [];
                }

                // Ensure src_job_run_id value is in options if it exists
                this.ensureJobRunIdInOptions(jobId);

                // Load production meters if destination is set
                if (this.promotions[jobId]?.dst_sandbox_name && this.promotions[jobId]?.dst_job_name) {
                    this.loadProdMeters(jobId);
                }
            }
        },
        ensureJobRunIdInOptions(jobId) {
            // Ensure the src_job_run_id value is in the options array to prevent multiselect errors
            if (!this.promotionsInfo[jobId] || !this.promotionsInfo[jobId].fields) {
                return;
            }

            const promotion = this.promotions[jobId];
            const existingValue = promotion?.src_job_run_id;

            if (existingValue && existingValue.trim() !== "") {
                // Find the src_job_run_id field
                const srcJobRunField = this.promotionsInfo[jobId].fields.find(f => f.name === "src_job_run_id");
                if (srcJobRunField) {
                    // Ensure options is an array
                    if (!Array.isArray(srcJobRunField.options)) {
                        srcJobRunField.options = [];
                    }

                    // Check if value exists in options
                    const valueExists = srcJobRunField.options.some(opt => opt.value === existingValue);
                    if (!valueExists) {
                        // Add the existing value to options so multiselect can display it
                        srcJobRunField.options.unshift({
                            label: existingValue,
                            value: existingValue
                        });
                    }

                    // If options is empty, add the value to prevent multiselect errors
                    if (srcJobRunField.options.length === 0) {
                        srcJobRunField.options = [{
                            label: existingValue,
                            value: existingValue
                        }];
                    }
                }
            }
        },
        toggleJobSelection(jobId) {
            const index = this.selectedJobs.indexOf(jobId);
            if (index > -1) {
                this.selectedJobs.splice(index, 1);
            } else {
                this.selectedJobs.push(jobId);
            }
        },
        toggleSelectAll() {
            this.toggleInProgress = true;
            if (!this.jobs || this.jobs.length === 0) return;

            if (this.allJobsSelected) {
                // Deselect all
                this.selectedJobs = [];
            } else {
                // Select all
                this.selectedJobs = this.jobs.map(job => job.jobDetails?.name || job.jobId);
            }
            this.toggleInProgress = false;
        },
        preparePromotionPayload(jobId) {
            if (!this.promotions[jobId]) {
                return null;
            }
            const promotion = JSON.parse(JSON.stringify(this.promotions[jobId]));

            // Remove empty vars (Codemirror adds empty string on initial editor load)
            if (promotion.vars === '') {
                delete promotion.vars;
            }

            // Handle meters - add meters if not detach_meters and meters exist
            const meters = promotion.meters || [];
            if (!promotion.detach_meters && meters.length > 0) {
                promotion.meters = meters;
            } else {
                delete promotion.meters;
            }

            return promotion;
        },
        async onPromoteClick() {
            if (!this.currentPromotion || !this.isCurrentPromotionValid) {
                this.promoteError = "Please fill in all required fields (Destination Sandbox Name, Destination Job Name, Description)";
                return;
            }

            this.promoteInProgress = true;
            this.promoteError = "";

            try {
                const promotionPayload = this.preparePromotionPayload(this.currentPromotion);
                if (!promotionPayload) {
                    throw new Error("Failed to prepare promotion payload");
                }

                let disableMetersToast = null;
                if (promotionPayload.disable_meters_validation) {
                    disableMetersToast = {
                        message: "'Disable Meters Validation' is applied",
                        type: "warning"
                    };
                }

                const response = await axios.post(
                    `${this.$store.state.lakeviewUrl}/v1/cids/${this.$store.state.currentClient.cid}/promotions`,
                    promotionPayload,
                    {
                        headers: {
                            Authorization: `Bearer ${this.$store.state.accessToken}`,
                        },
                    }
                );

                if (disableMetersToast) {
                    this.$store.dispatch('addToast', disableMetersToast);
                }

                this.$store.dispatch('hideModal');
                this.$router.push({
                    name: "Promotion Overview",
                    params: {
                        sandboxId: response.data.dst_sandbox_name,
                        promotionId: response.data.id,
                    }
                });
            } catch (error) {
                this.promoteError = error.response?.data?.error || error.message || "Failed to promote job";
                this.$store.dispatch("addToast", {
                    message: this.promoteError || "Something went wrong when promoting the job. Please contact support.",
                    type: "error",
                });
            } finally {
                this.promoteInProgress = false;
            }
        },
        async onPromoteSelectedClick() {
            if (!this.areSelectedPromotionsValid || this.selectedJobs.length === 0) {
                this.promoteError = "Please ensure all selected promotions have required fields filled (Destination Sandbox Name, Destination Job Name, Description)";
                return;
            }

            this.promoteSelectedInProgress = true;
            this.promoteError = "";

            try {
                // Prepare all promotion payloads
                const promotionPromises = this.selectedJobs.map(async (jobId) => {
                    const promotionPayload = this.preparePromotionPayload(jobId);
                    if (!promotionPayload) {
                        return Promise.reject(new Error(`Failed to prepare promotion payload for ${jobId}`));
                    }

                    return axios.post(
                        `${this.$store.state.lakeviewUrl}/v1/cids/${this.$store.state.currentClient.cid}/promotions`,
                        promotionPayload,
                        {
                            headers: {
                                Authorization: `Bearer ${this.$store.state.accessToken}`,
                            },
                        }
                    ).then(response => ({
                        jobId,
                        success: true,
                        data: response.data,
                        disableMetersToast: !!(promotionPayload.disable_meters_validation)
                    })).catch(error => ({
                        jobId,
                        success: false,
                        error: error.response?.data?.error || error.message || "Failed to promote job",
                        jobName: promotionPayload.dst_job_name || jobId
                    }));
                });

                // Use Promise.allSettled to handle all promotions asynchronously
                const results = await Promise.allSettled(promotionPromises);

                let successCount = 0;
                let failureCount = 0;
                let disableMetersJobs = [];
                const errors = [];
                let firstSuccessfulSandboxName = null;

                results.forEach((result, index) => {
                    if (result.status === 'fulfilled') {
                        const promotionResult = result.value;
                        if (promotionResult.success) {
                            successCount++;
                            // Capture sandbox name from first successful response
                            if (!firstSuccessfulSandboxName && promotionResult.data?.dst_sandbox_name) {
                                firstSuccessfulSandboxName = promotionResult.data.dst_sandbox_name;
                            }
                            if (promotionResult.disableMetersToast) {
                                disableMetersJobs.push(result.jobId);
                            }
                        } else {
                            failureCount++;
                            errors.push(`${promotionResult.jobName}: ${promotionResult.error}`);
                        }
                    } else {
                        failureCount++;
                        errors.push(`Job ${this.selectedJobs[index]}: ${result.reason?.message || 'Unknown error'}`);
                    }
                });

                // Show toasts for disable_meters_validation
                if (disableMetersJobs.length > 0) {
                    this.$store.dispatch('addToast', {
                        message: `'Disable Meters Validation' is applied for ${disableMetersJobs.join(', ')} jobs`,
                        type: "warning"
                    });
                }

                // Show summary toast
                if (successCount === this.selectedJobs.length) {
                    this.$store.dispatch("addToast", {
                        message: `Promotion requests successfully raised for ${successCount} job(s)`,
                        type: "success",
                    });

                    this.$store.dispatch('hideModal');
                    this.$router.push({
                        name: "Promotions",
                        params: {
                            sandboxId: firstSuccessfulSandboxName || this.deployment?.sandbox
                        }
                    });
                } else if (successCount > 0) {
                    let errorDisplay = errors.join('; ') || "";
                    this.promoteError = errorDisplay?.length > 200 ? errorDisplay.substring(0, 200) + '...' : errorDisplay;
                    this.$store.dispatch("addToast", {
                        message: `Promotion requests successfully raised for ${successCount} job(s), but ${failureCount} job(s) failed.`,
                        type: "warning",
                    });
                } else {
                    let errorDisplay = errors.join('; ') || "";
                    this.promoteError = errorDisplay?.length > 200 ? errorDisplay.substring(0, 200) + '...' : errorDisplay;
                    this.$store.dispatch("addToast", {
                        message: `Promotion requests failed for ${failureCount} job(s).`,
                        type: "error",
                    });
                }
            } catch (error) {
                this.promoteError = error.message || "Failed to promote selected jobs";
                this.$store.dispatch("addToast", {
                    message: this.promoteError || "Something went wrong when promoting jobs. Please contact support.",
                    type: "error",
                });
            } finally {
                this.promoteSelectedInProgress = false;
            }
        },
        onAddMeter() {
            if (!this.currentPromotion || !this.promotions[this.currentPromotion]) return;
            if (!this.promotions[this.currentPromotion].meters) {
                this.promotions[this.currentPromotion].meters = [];
            }
            this.promotions[this.currentPromotion].meters.push({
                label: null,
                metrics_type: null,
                metrics_field: null,
                metrics_data_channel: null,
            });
        },
        removeMeter(index) {
            if (!this.currentPromotion || !this.promotions[this.currentPromotion]) return;
            if (this.promotions[this.currentPromotion].meters) {
                this.promotions[this.currentPromotion].meters.splice(index, 1);
            }
        },
        updateMetricsType(index) {
            if (!this.currentPromotion || !this.currentPromotionInfo || !this.promotions[this.currentPromotion]) return;
            const meters = this.promotions[this.currentPromotion].meters;
            if (!meters || !meters[index]) return;
            const selectedDataChannel = meters[index].metrics_data_channel;
            const metricsType = this.currentPromotionInfo.dataChannelTypeMap.get(selectedDataChannel);
            if (metricsType) {
                meters[index].metrics_type = metricsType;
            }
        },
        displayWarning() {
            if (!this.currentPromotion || !this.promotions[this.currentPromotion]) return false;
            const promotion = this.promotions[this.currentPromotion];
            const currentMetersList = promotion.meters || [];
            const prodMetersList = this.prodMeters[this.currentPromotion] || [];
            return prodMetersList.length === 0 && currentMetersList.length === 0 && !promotion?.detach_meters;
        },
        async loadProdMeters(jobId) {
            if (!jobId || !this.promotions[jobId]) return;
            this.prodMeters[jobId] = [];
            let cid = this.$store.state.currentClient.cid;
            let sandbox = this.promotions[jobId].dst_sandbox_name;
            let job = this.promotions[jobId].dst_job_name;
            if (!sandbox || !job) return;
            try {
                const response = await axios.get(`${this.$store.state.lakeviewUrl}/v1/cids/${cid}/sandboxes/${sandbox}/jobs/${job}`, {
                    headers: {
                        Authorization: `Bearer ${this.$store.state.accessToken}`,
                    },
                });
                if (response.data?.meters) {
                    this.prodMeters[jobId] = response.data.meters;
                }
            } catch (error) {
                console.error('Error loading production meters:', error);
            }
        },
        setupPromotionWatchers(jobId) {
            // Watch detach_meters (like PromoteJob.vue)
            this.$watch(
                () => this.promotions[jobId]?.detach_meters,
                (newValue) => {
                    if (!this.promotions[jobId] || !this.promotionsInfo[jobId]) return;

                    // Ensure saveMeters exists
                    if (!this.promotionsInfo[jobId].saveMeters) {
                        this.promotionsInfo[jobId].saveMeters = [];
                    }

                    // Ensure meters array exists
                    if (!this.promotions[jobId].meters) {
                        this.promotions[jobId].meters = [];
                    }

                    if (newValue) {
                        // Save current meters and clear them
                        this.promotionsInfo[jobId].saveMeters = JSON.parse(JSON.stringify(this.promotions[jobId].meters || []));
                        this.promotions[jobId].meters = [];
                    } else {
                        // Restore saved meters
                        if (this.promotionsInfo[jobId].saveMeters && this.promotionsInfo[jobId].saveMeters.length > 0) {
                            this.promotions[jobId].meters = JSON.parse(JSON.stringify(this.promotionsInfo[jobId].saveMeters));
                        }
                    }
                },
                { deep: true }
            );

            // Watch src_job_run_id (like PromoteJob.vue)
            this.$watch(
                () => this.promotions[jobId]?.src_job_run_id,
                (newValue) => {
                    if (!this.promotionsInfo[jobId]) return;
                    this.promotionsInfo[jobId].dataChannel = [];
                    this.getDataChannels(jobId, newValue);
                },
                { deep: true }
            );

            // Watch dst_job_name (like PromoteJob.vue)
            this.$watch(
                () => this.promotions[jobId]?.dst_job_name,
                (newValue) => {
                    if (!this.promotionsInfo[jobId]) return;
                    this.promotionsInfo[jobId].dataChannel = [];
                    this.getDataChannels(jobId, this.promotions[jobId]?.src_job_run_id);
                    this.loadProdMeters(jobId);
                },
                { deep: true }
            );

            // Watch dst_sandbox_name (like PromoteJob.vue)
            this.$watch(
                () => this.promotions[jobId]?.dst_sandbox_name,
                (newValue) => {
                    if (!this.promotionsInfo[jobId]) return;
                    this.promotionsInfo[jobId].dataChannel = [];
                    this.getDataChannels(jobId, this.promotions[jobId]?.src_job_run_id);
                    this.loadProdMeters(jobId);
                },
                { deep: true }
            );
        },
    },
}
</script>

<style scoped>
.form-label {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.custom-modal-tabs {
    border-bottom: 2px solid #e9ecef;
    margin-bottom: 1rem;
    gap: 0;
}

.custom-modal-tabs .nav-item {
    margin: 0;
    margin-right: 0;
    padding: 0;
}

.custom-modal-tabs .nav-link {
    color: #6c757d;
    background-color: transparent;
    border: none;
    border-bottom: 2px solid transparent;
    padding: 0.6rem 0.75rem;
    font-weight: 500;
    transition: all 0.2s ease-in-out;
    cursor: pointer;
    display: flex;
    align-items: center;
}

.custom-modal-tabs .nav-link:hover {
    color: #495057;
    background-color: #f8f9fa;
    border-bottom-color: #dee2e6;
    border-top-left-radius: 5px;
    border-top-right-radius: 5px;
}

.custom-modal-tabs .nav-link.active {
    color: #0d6efd;
    background-color: #e2edff;
    border-bottom-color: #0d6efd;
    border-top-left-radius: 5px;
    border-top-right-radius: 5px;
    font-weight: 600;
}

.custom-modal-tabs .nav-link i {
    font-size: 0.9rem;
}


.tab-content {
    min-height: 200px;
}

.tab-pane {
    display: none;
}

.tab-pane.show.active {
    display: block;
    animation: fadeIn 0.3s ease-in;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(5px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.edit-tab-content {
    padding-top: 0.5rem;
}

.config-changes-tab-content {
    padding-top: 0.5rem;
}

.config-header {
    color: #2C7BE5;
    font-size: 0.9rem;
    margin-bottom: 0.5rem;
}

.promote-tab-content {
    padding-top: 0.5rem;
}

.job-list-item {
    padding: 0.6rem 1rem;
    border: 1px solid;
    border-color: #e9ecef;
    border-radius: 0.2rem;
    margin-bottom: 0.375rem;
    transition: all 0.2s ease-in-out;
    box-shadow: 0 1px 3px rgba(13, 110, 253, 0.15);
}

.job-list-item:first-child {
    border-top: 1px solid #e9ecef;
}

.job-list-item:hover {
    background-color: #f8f9fa;
    border-left-color: #dee2e6;
}

.job-list-item-active {
    background-color: #e7f1ff !important;
    border-left: 3px solid #0d6efd !important;
    color: #0d6efd;
    font-weight: 500;
    box-shadow: 0 2px 4px rgba(13, 110, 253, 0.1);
}

.job-list-item-active:hover {
    background-color: #d0e7ff !important;
    border-left-color: #0a58ca !important;
    box-shadow: 0 2px 6px rgba(13, 110, 253, 0.15);
}
</style>